import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import SignatureCanvas from "react-signature-canvas";
import { logoutUser } from "../slices/userSlice";
import {
  getMyBusiness,
  createOrUpdateBusiness,
  resetSave,
} from "../slices/businessSlice";
import { usePermissions } from "../utils/permissions";
import BottomNavbar from "../componets/BottomNavbar";

const dataURLtoFile = (dataurl, filename = "signature.png") => {
  const arr = dataurl.split(",");
  const mime = (arr[0].match(/:(.*?);/) || [])[1] || "image/png";
  const bstr = atob(arr[1] || "");
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) u8arr[n] = bstr.charCodeAt(n);
  return new File([u8arr], filename, { type: mime });
};

// Full URL for upload images so they display (backend may be on different origin; set REACT_APP_BACKEND_URL on VPS)
const getUploadImageUrl = (url) => {
  if (!url || typeof url !== "string") return "";
  if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:")) return url;
  const base = typeof process !== "undefined" && process.env.REACT_APP_BACKEND_URL
    ? String(process.env.REACT_APP_BACKEND_URL).replace(/\/$/, "")
    : (typeof window !== "undefined" ? window.location.origin : "");
  return base + (url.startsWith("/") ? url : "/" + url);
};

const MyBusiness = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state) => state.user);
  const { activeRoles } = useSelector((state) => state.role);
  const { business, loading, saveLoading, saveSuccess } = useSelector(
    (state) => state.business
  );

  usePermissions();
  const hasLoadedBusiness = useRef(false);

  // Check permission directly from Redux state to avoid infinite loops
  const canReadProfile = useMemo(() => {
    if (user?.role === "admin") return true;
    const userRole = activeRoles.find((role) => role.name === user?.role);
    if (!userRole) return false;
    const permission = userRole.permissions.find((p) => p.resource === "profile");
    return permission && permission.actions.includes("read");
  }, [user?.role, activeRoles]);

  const [formData, setFormData] = useState({
    companyName: "",
    address: "",
    gstin: "",
    state: "",
    city: "",
    pincode: "",
    email: "",
    phone: "",
    pan: "",
    bankName: "",
    accountNumber: "",
    ifscCode: "",
    accountHolderName: "",
    companyLogo: null,
    companyLogoPreview: null,
    authorizedSignature: null,
    authorizedSignaturePreview: null,
    isActive: true,
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isEditMode, setIsEditMode] = useState(false);
  const [stoppingServers, setStoppingServers] = useState(false);
  const signaturePadRef = useRef(null);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }

    if (!canReadProfile) {
      navigate("/my-business");
      return;
    }

    // Only load business once when component mounts and user is authenticated
    if (!hasLoadedBusiness.current) {
      dispatch(getMyBusiness());
      hasLoadedBusiness.current = true;
    }
  }, [dispatch, isAuthenticated, navigate, canReadProfile]);

  useEffect(() => {
    if (business) {
      setFormData({
        companyName: business.companyName || "",
        address: business.address || "",
        gstin: business.gstin || "",
        state: business.state || "",
        city: business.city || "",
        pincode: business.pincode || "",
        email: business.email || "",
        phone: business.phone || "",
        pan: business.pan || "",
        bankName: business.bankName || "AXIS BANK, HATKESHWAR",
        accountNumber: business.accountNumber || "917020062510731",
        ifscCode: business.ifscCode || "UTIB0002645",
        accountHolderName: business.accountHolderName || "RAHUL TRADERS",
        companyLogo: null,
        companyLogoPreview: business.companyLogo?.url ? getUploadImageUrl(business.companyLogo.url) : null,
        authorizedSignature: null,
        authorizedSignaturePreview: business.authorizedSignature?.url ? getUploadImageUrl(business.authorizedSignature.url) : null,
        isActive: business.isActive !== undefined ? business.isActive : true,
      });
    }
  }, [business]);

  useEffect(() => {
    if (saveSuccess) {
      dispatch(resetSave());
      setIsEditMode(false);
      // Reload business data after successful save
      hasLoadedBusiness.current = false;
      dispatch(getMyBusiness());
      hasLoadedBusiness.current = true;
    }
  }, [saveSuccess, dispatch]);

  const validateForm = () => {
    const errors = {};

    // Company Name validation
    if (!formData.companyName.trim()) {
      errors.companyName = "Company name is required";
    } else if (formData.companyName.trim().length > 200) {
      errors.companyName = "Company name cannot exceed 200 characters";
    }

    // Address validation
    if (!formData.address.trim()) {
      errors.address = "Address is required";
    } else if (formData.address.trim().length > 500) {
      errors.address = "Address cannot exceed 500 characters";
    }

    // State validation
    if (!formData.state.trim()) {
      errors.state = "State is required";
    } else if (formData.state.trim().length > 100) {
      errors.state = "State cannot exceed 100 characters";
    }

    // City validation
    if (!formData.city.trim()) {
      errors.city = "City is required";
    } else if (formData.city.trim().length > 100) {
      errors.city = "City cannot exceed 100 characters";
    }

    // GSTIN validation (optional but must be valid if provided)
    if (formData.gstin && formData.gstin.trim()) {
      const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
      if (!gstinRegex.test(formData.gstin.trim().toUpperCase())) {
        errors.gstin = "Please enter a valid 15-digit GSTIN";
      }
    }

    // Pincode validation (optional but must be valid if provided)
    if (formData.pincode && formData.pincode.trim()) {
      if (!/^\d{6}$/.test(formData.pincode.trim())) {
        errors.pincode = "Please enter a valid 6-digit pincode";
      }
    }

    // Email validation (optional but must be valid if provided)
    if (formData.email && formData.email.trim()) {
      const emailRegex = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
      if (!emailRegex.test(formData.email.trim())) {
        errors.email = "Please enter a valid email address";
      }
    }

    // Phone validation (optional but must be valid if provided)
    if (formData.phone && formData.phone.trim()) {
      if (!/^[6-9]\d{9}$/.test(formData.phone.trim())) {
        errors.phone = "Please enter a valid 10-digit phone number";
      }
    }

    // PAN validation (optional but must be valid if provided)
    if (formData.pan && formData.pan.trim()) {
      const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
      if (!panRegex.test(formData.pan.trim().toUpperCase())) {
        errors.pan = "Please enter a valid PAN (e.g., ABCDE1234F)";
      }
    }

    // Bank Name validation (optional)
    if (formData.bankName && formData.bankName.trim().length > 200) {
      errors.bankName = "Bank name cannot exceed 200 characters";
    }

    // Account Number validation (optional but must be valid if provided)
    if (formData.accountNumber && formData.accountNumber.trim()) {
      if (!/^\d{9,18}$/.test(formData.accountNumber.trim())) {
        errors.accountNumber = "Please enter a valid account number (9-18 digits)";
      }
    }

    // IFSC Code validation (optional but must be valid if provided)
    if (formData.ifscCode && formData.ifscCode.trim()) {
      const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
      if (!ifscRegex.test(formData.ifscCode.trim().toUpperCase())) {
        errors.ifscCode = "Please enter a valid IFSC code (e.g., ABCD0123456)";
      }
    }

    // Account Holder Name validation (optional)
    if (formData.accountHolderName && formData.accountHolderName.trim().length > 200) {
      errors.accountHolderName = "Account holder name cannot exceed 200 characters";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const businessData = {
      companyName: formData.companyName.trim(),
      address: formData.address.trim(),
      gstin: formData.gstin.trim().toUpperCase() || "",
      state: formData.state.trim(),
      city: formData.city.trim(),
      pincode: formData.pincode.trim() || "",
      email: formData.email.trim().toLowerCase() || "",
      phone: formData.phone.trim() || "",
      pan: formData.pan.trim().toUpperCase() || "",
      bankName: formData.bankName.trim() || "",
      accountNumber: formData.accountNumber.trim() || "",
      ifscCode: formData.ifscCode.trim().toUpperCase() || "",
      accountHolderName: formData.accountHolderName.trim() || "",
      companyLogo: formData.companyLogo || null,
      authorizedSignature: formData.authorizedSignature || null,
      removeAuthorizedSignature: formData.removeAuthorizedSignature || false,
      isActive: formData.isActive,
    };

    dispatch(createOrUpdateBusiness(businessData));
  };

  const handleCompanyLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        setValidationErrors({
          ...validationErrors,
          companyLogo: "Please select a valid image file",
        });
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setValidationErrors({
          ...validationErrors,
          companyLogo: "Image size should not exceed 5MB",
        });
        return;
      }

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({
          ...formData,
          companyLogo: file,
          companyLogoPreview: reader.result,
        });
      };
      reader.readAsDataURL(file);

      // Clear error if file is valid
      if (validationErrors.companyLogo) {
        setValidationErrors({
          ...validationErrors,
          companyLogo: undefined,
        });
      }
    }
  };

  const handleRemoveCompanyLogo = () => {
    setFormData({
      ...formData,
      companyLogo: null,
      companyLogoPreview: null,
    });
  };

  const handleAuthorizedSignatureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setValidationErrors({ ...validationErrors, authorizedSignature: "Please select a valid image (PNG/JPG)" });
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        setValidationErrors({ ...validationErrors, authorizedSignature: "Signature image should not exceed 2MB" });
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({
          ...formData,
          authorizedSignature: file,
          authorizedSignaturePreview: reader.result,
          removeAuthorizedSignature: false,
        });
      };
      reader.readAsDataURL(file);
      if (validationErrors.authorizedSignature) {
        setValidationErrors({ ...validationErrors, authorizedSignature: undefined });
      }
    }
  };

  const handleRemoveAuthorizedSignature = () => {
    setFormData({
      ...formData,
      authorizedSignature: null,
      authorizedSignaturePreview: null,
      removeAuthorizedSignature: true,
    });
    if (signaturePadRef.current) signaturePadRef.current.clear();
  };

  const handleClearSignaturePad = () => {
    if (signaturePadRef.current) signaturePadRef.current.clear();
  };

  const handleSaveDrawnSignature = () => {
    if (!signaturePadRef.current) return;
    if (signaturePadRef.current.isEmpty()) {
      setValidationErrors({ ...validationErrors, authorizedSignature: "Draw your signature first." });
      return;
    }
    const dataURL = signaturePadRef.current.toDataURL("image/png");
    const file = dataURLtoFile(dataURL);
    setFormData({
      ...formData,
      authorizedSignature: file,
      authorizedSignaturePreview: dataURL,
      removeAuthorizedSignature: false,
    });
    if (validationErrors.authorizedSignature) setValidationErrors({ ...validationErrors, authorizedSignature: undefined });
  };

  const handleLogout = async () => {
    try {
      await dispatch(logoutUser()).unwrap();
      navigate("/login", { replace: true });
    } catch {
      /* toast from userSlice */
    }
  };

  const handleStopServers = () => {
    if (!window.confirm("Stop PM2 and this dev server? Both will exit.")) return;
    setStoppingServers(true);
    axios
      .post("/api/dev/stop-servers", {}, { withCredentials: true })
      .then(() => {
        setStoppingServers(false);
      })
      .catch(() => {
        setStoppingServers(false);
      });
  };

  if (loading && !business) {
    return (
      <div style={styles.loadingContainer}>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Main Content */}
      <div style={{
        ...styles.content,
        padding: isMobile ? "16px" : "24px",
        paddingBottom: isMobile ? "100px" : "100px",
      }}>
        {/* Page Header + account actions */}
        <div style={{
          ...styles.pageHeader,
          marginBottom: isMobile ? "16px" : "24px",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          borderBottom: "1px solid #e9edef",
        }}>
          <h1 style={{
            ...styles.pageTitle,
            fontSize: isMobile ? "28px" : "32px",
          }}>Business Details</h1>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "center" }}>
            <Link
              to="/change-password"
              style={{
                textDecoration: "none",
                padding: "8px 16px",
                borderRadius: "8px",
                border: "1px solid #00a884",
                color: "#00a884",
                fontWeight: "600",
                fontSize: isMobile ? "13px" : "14px",
                backgroundColor: "#ffffff",
              }}
            >
              Change password
            </Link>
            <button
              type="button"
              onClick={handleLogout}
              style={{
                padding: "8px 16px",
                borderRadius: "8px",
                border: "1px solid #dc3545",
                color: "#dc3545",
                fontWeight: "600",
                fontSize: isMobile ? "13px" : "14px",
                backgroundColor: "#ffffff",
                cursor: "pointer",
              }}
            >
              Logout
            </button>
          </div>
        </div>

        {/* Quick Links: WhatsApp Control, PDFs, Bills, Profile */}
        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)",
          gap: "12px",
          marginBottom: isMobile ? "20px" : "24px",
        }}>
          <Link
            to="/whatsapp-control"
            style={{
              textDecoration: "none",
              backgroundColor: "#ffeef2",
              borderRadius: "12px",
              padding: "16px",
              border: "1px solid #ffd6e0",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              color: "#111b21",
              fontWeight: "600",
              fontSize: isMobile ? "14px" : "15px",
              textAlign: "center",
            }}
          >
            WhatsApp Control
          </Link>
          <Link
            to="/pdfs"
            style={{
              textDecoration: "none",
              backgroundColor: "#ffeef2",
              borderRadius: "12px",
              padding: "16px",
              border: "1px solid #ffd6e0",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              color: "#111b21",
              fontWeight: "600",
              fontSize: isMobile ? "14px" : "15px",
              textAlign: "center",
            }}
          >
            PDFs
          </Link>
          <Link
            to="/bills"
            style={{
              textDecoration: "none",
              backgroundColor: "#ffeef2",
              borderRadius: "12px",
              padding: "16px",
              border: "1px solid #ffd6e0",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              color: "#111b21",
              fontWeight: "600",
              fontSize: isMobile ? "14px" : "15px",
              textAlign: "center",
            }}
          >
            Bills
          </Link>
          <Link
            to="/profile"
            style={{
              textDecoration: "none",
              backgroundColor: "#ffeef2",
              borderRadius: "12px",
              padding: "16px",
              border: "1px solid #ffd6e0",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              color: "#111b21",
              fontWeight: "600",
              fontSize: isMobile ? "14px" : "15px",
              textAlign: "center",
            }}
          >
            Profile
          </Link>
          <button
            type="button"
            onClick={handleStopServers}
            disabled={stoppingServers}
            style={{
              backgroundColor: "#dc3545",
              color: "#fff",
              border: "none",
              borderRadius: "12px",
              padding: "16px",
              fontWeight: "600",
              fontSize: isMobile ? "14px" : "15px",
              cursor: stoppingServers ? "not-allowed" : "pointer",
              opacity: stoppingServers ? 0.7 : 1,
            }}
          >
            {stoppingServers ? "Stopping…" : "Stop servers (PM2 + this server)"}
          </button>
        </div>

        {/* Business Card */}
        {business && !isEditMode ? (
          <div style={{
            ...styles.businessCard,
            padding: isMobile ? "20px" : "32px",
          }}>
            <div style={styles.businessCardHeader}>
              <div style={styles.businessLogo}>
                <div style={styles.logoCircle}>
                  {business.companyName ? business.companyName.substring(0, 2).toUpperCase() : "RT"}
                </div>
              </div>
              <div style={styles.businessHeaderInfo}>
                <div style={styles.businessNameRow}>
                  <h2 style={{
                    ...styles.businessName,
                    fontSize: isMobile ? "18px" : "20px",
                  }}>{business.companyName || "RAHUL TRADERS"}</h2>
                  <div style={styles.businessHeaderActions}>
                    <button
                      onClick={() => setIsEditMode(true)}
                      style={styles.businessEditButton}
                      title="Edit Business"
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#e9edef";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }}
                    >
                      ✏️
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div style={styles.businessDetails}>
              {business.email && (
                <div style={styles.businessDetailRow}>
                  <span style={styles.businessDetailLabel}>Owner: </span>
                  <span style={styles.businessDetailValue}>{business.email.split("@")[0] || "Rahul"}</span>
                </div>
              )}
              {business.address && (
                <div style={styles.businessDetailRow}>
                  <span style={styles.businessDetailLabel}>Address: </span>
                  <span style={styles.businessDetailValue}>{business.address}</span>
                </div>
              )}
              {(business.state || business.city) && (
                <div style={styles.businessDetailRow}>
                  <span style={styles.businessDetailLabel}>State / City: </span>
                  <span style={styles.businessDetailValue}>
                    {business.state ? `${business.state}` : ""}
                    {business.state && business.city ? " / " : ""}
                    {business.city ? `${business.city}` : ""}
                  </span>
                </div>
              )}
              {business.gstin && (
                <div style={styles.businessDetailRow}>
                  <span style={styles.businessDetailLabel}>GSTIN: </span>
                  <span style={styles.businessDetailValue}>{business.gstin}</span>
                </div>
              )}
              {business.pan && (
                <div style={styles.businessDetailRow}>
                  <span style={styles.businessDetailLabel}>PAN: </span>
                  <span style={styles.businessDetailValue}>{business.pan}</span>
                </div>
              )}
            </div>

            {/* Company Logo Display */}
            {business.companyLogo && business.companyLogo.url && (
              <div style={styles.companyLogoSection}>
                <h3 style={styles.companyLogoTitle}>Company Logo</h3>
                <div style={styles.companyLogoContainer}>
                  <img 
                    src={getUploadImageUrl(business.companyLogo.url)} 
                    alt="Company Logo" 
                    style={styles.companyLogo}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='80' viewBox='0 0 120 80'%3E%3Crect fill='%23f0f0f0' width='120' height='80'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23999' font-size='12'%3ELogo%3C/text%3E%3C/svg%3E";
                    }}
                  />
                </div>
              </div>
            )}

            {/* Digital Signature Display */}
            {business.authorizedSignature && business.authorizedSignature.url && (
              <div style={{ ...styles.companyLogoSection, marginTop: "16px" }}>
                <h3 style={styles.companyLogoTitle}>Digital Signature</h3>
                <div style={styles.companyLogoContainer}>
                  <img 
                    src={getUploadImageUrl(business.authorizedSignature.url)} 
                    alt="Authorized Signature" 
                    style={{ maxHeight: "48px", maxWidth: "140px", objectFit: "contain" }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='48' viewBox='0 0 140 48'%3E%3Crect fill='%23f0f0f0' width='140' height='48'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23999' font-size='11'%3ESignature%3C/text%3E%3C/svg%3E";
                    }}
                  />
                </div>
              </div>
            )}

            {/* Bank Account Details Section */}
            {(business.bankName || business.accountNumber || business.ifscCode || business.accountHolderName) && (
              <div style={styles.bankAccountSection}>
                <h3 style={styles.bankAccountTitle}>Bank Account Details</h3>
                <div style={styles.bankDetails}>
                  {business.bankName && (
                    <div style={styles.businessDetailRow}>
                      <span style={styles.businessDetailLabel}>Bank Name: </span>
                      <span style={styles.businessDetailValue}>{business.bankName}</span>
                    </div>
                  )}
                  {business.accountHolderName && (
                    <div style={styles.businessDetailRow}>
                      <span style={styles.businessDetailLabel}>Account Holder: </span>
                      <span style={styles.businessDetailValue}>{business.accountHolderName}</span>
                    </div>
                  )}
                  {business.accountNumber && (
                    <div style={styles.businessDetailRow}>
                      <span style={styles.businessDetailLabel}>Account Number: </span>
                      <span style={styles.businessDetailValue}>{business.accountNumber}</span>
                    </div>
                  )}
                  {business.ifscCode && (
                    <div style={styles.businessDetailRow}>
                      <span style={styles.businessDetailLabel}>IFSC Code: </span>
                      <span style={styles.businessDetailValue}>{business.ifscCode}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={styles.card}>
            <form onSubmit={handleSubmit} style={{
              ...styles.form,
              gap: isMobile ? "16px" : "20px",
            }}>
            <div style={{
              ...styles.formRow,
              display: isMobile ? "flex" : "grid",
              flexDirection: isMobile ? "column" : "row",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: isMobile ? "16px" : "20px",
            }}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Company Name <span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.companyName}
                  onChange={(e) =>
                    setFormData({ ...formData, companyName: e.target.value })
                  }
                  style={{
                    ...styles.input,
                    borderColor: validationErrors.companyName
                      ? "#dc3545"
                      : "#e9edef",
                    width: "100%",
                  }}
                  placeholder="Enter company name"
                />
                {validationErrors.companyName && (
                  <span style={styles.error}>
                    {validationErrors.companyName}
                  </span>
                )}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  GSTIN <span style={styles.optional}>(Optional)</span>
                </label>
                <input
                  type="text"
                  value={formData.gstin}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      gstin: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""),
                    })
                  }
                  maxLength="15"
                  style={{
                    ...styles.input,
                    borderColor: validationErrors.gstin ? "#dc3545" : "#e9edef",
                    width: "100%",
                  }}
                  placeholder="Enter 15-digit GSTIN"
                />
                {validationErrors.gstin && (
                  <span style={styles.error}>{validationErrors.gstin}</span>
                )}
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                Address <span style={styles.required}>*</span>
              </label>
              <textarea
                value={formData.address}
                onChange={(e) =>
                  setFormData({ ...formData, address: e.target.value })
                }
                style={{
                  ...styles.textarea,
                  borderColor: validationErrors.address ? "#dc3545" : "#e9edef",
                  width: "100%",
                }}
                placeholder="Enter complete address"
                rows="3"
              />
              {validationErrors.address && (
                <span style={styles.error}>{validationErrors.address}</span>
              )}
            </div>

            <div style={{
              ...styles.formRow,
              display: isMobile ? "flex" : "grid",
              flexDirection: isMobile ? "column" : "row",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
              gap: isMobile ? "16px" : "20px",
            }}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  State <span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) =>
                    setFormData({ ...formData, state: e.target.value })
                  }
                  style={{
                    ...styles.input,
                    borderColor: validationErrors.state ? "#dc3545" : "#e9edef",
                    width: "100%",
                  }}
                  placeholder="Enter state"
                />
                {validationErrors.state && (
                  <span style={styles.error}>{validationErrors.state}</span>
                )}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  City <span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) =>
                    setFormData({ ...formData, city: e.target.value })
                  }
                  style={{
                    ...styles.input,
                    borderColor: validationErrors.city ? "#dc3545" : "#e9edef",
                    width: "100%",
                  }}
                  placeholder="Enter city"
                />
                {validationErrors.city && (
                  <span style={styles.error}>{validationErrors.city}</span>
                )}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Pincode <span style={styles.optional}>(Optional)</span>
                </label>
                <input
                  type="text"
                  value={formData.pincode}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      pincode: e.target.value.replace(/\D/g, ""),
                    })
                  }
                  maxLength="6"
                  style={{
                    ...styles.input,
                    borderColor: validationErrors.pincode ? "#dc3545" : "#e9edef",
                    width: "100%",
                  }}
                  placeholder="Enter 6-digit pincode"
                />
                {validationErrors.pincode && (
                  <span style={styles.error}>{validationErrors.pincode}</span>
                )}
              </div>
            </div>

            <div style={{
              ...styles.formRow,
              display: isMobile ? "flex" : "grid",
              flexDirection: isMobile ? "column" : "row",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
              gap: isMobile ? "16px" : "20px",
            }}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Email <span style={styles.optional}>(Optional)</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  style={{
                    ...styles.input,
                    borderColor: validationErrors.email ? "#dc3545" : "#e9edef",
                    width: "100%",
                  }}
                  placeholder="Enter email address"
                />
                {validationErrors.email && (
                  <span style={styles.error}>{validationErrors.email}</span>
                )}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Phone <span style={styles.optional}>(Optional)</span>
                </label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      phone: e.target.value.replace(/\D/g, ""),
                    })
                  }
                  maxLength="10"
                  style={{
                    ...styles.input,
                    borderColor: validationErrors.phone ? "#dc3545" : "#e9edef",
                    width: "100%",
                  }}
                  placeholder="Enter 10-digit phone number"
                />
                {validationErrors.phone && (
                  <span style={styles.error}>{validationErrors.phone}</span>
                )}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  PAN <span style={styles.optional}>(Optional)</span>
                </label>
                <input
                  type="text"
                  value={formData.pan}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      pan: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""),
                    })
                  }
                  maxLength="10"
                  style={{
                    ...styles.input,
                    borderColor: validationErrors.pan ? "#dc3545" : "#e9edef",
                    width: "100%",
                  }}
                  placeholder="Enter PAN (e.g., ABCDE1234F)"
                />
                {validationErrors.pan && (
                  <span style={styles.error}>{validationErrors.pan}</span>
                )}
              </div>
            </div>

            {/* Company Logo Section */}
            <div style={styles.companyLogoFormSection}>
              <h3 style={styles.sectionTitle}>Company Logo</h3>
              
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Company Logo <span style={styles.optional}>(Optional)</span>
                </label>
                {formData.companyLogoPreview && (
                  <div style={styles.imagePreviewContainer}>
                    <img 
                      src={formData.companyLogoPreview.startsWith("data:") ? formData.companyLogoPreview : getUploadImageUrl(formData.companyLogoPreview)} 
                      alt="Company Logo Preview" 
                      style={styles.imagePreview}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='80' viewBox='0 0 120 80'%3E%3Crect fill='%23f0f0f0' width='120' height='80'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23999' font-size='12'%3ELogo%3C/text%3E%3C/svg%3E";
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleRemoveCompanyLogo}
                      style={styles.removeImageButton}
                      title="Remove Logo"
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "scale(1.1)";
                        e.currentTarget.style.backgroundColor = "#c82333";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "scale(1)";
                        e.currentTarget.style.backgroundColor = "#dc3545";
                      }}
                    >
                      ×
                    </button>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleCompanyLogoChange}
                  style={styles.fileInput}
                  id="companyLogoInput"
                />
                <label 
                  htmlFor="companyLogoInput" 
                  style={styles.fileInputLabel}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#e9edef";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "#f0f2f5";
                  }}
                >
                  {formData.companyLogoPreview ? "Change Company Logo" : "Upload Company Logo"}
                </label>
                {validationErrors.companyLogo && (
                  <span style={styles.error}>{validationErrors.companyLogo}</span>
                )}
              </div>
            </div>

            {/* Digital Signature (Authorized Signatory) - Draw or upload, shows on all bills */}
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Digital Signature <span style={styles.optional}>(Optional - shows on bills)</span>
              </label>
              <p style={{ fontSize: "12px", color: "#666", marginTop: "4px", marginBottom: "8px" }}>
                Draw below or upload an image (max 2MB). It will appear on all bills in View, Print, Download, and WhatsApp send.
              </p>
              {(formData.authorizedSignaturePreview || formData.authorizedSignature) && (
                <div style={{ marginBottom: "12px", display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                  <img
                    src={formData.authorizedSignature
                      ? URL.createObjectURL(formData.authorizedSignature)
                      : (formData.authorizedSignaturePreview?.startsWith("data:") ? formData.authorizedSignaturePreview : getUploadImageUrl(formData.authorizedSignaturePreview || ""))}
                    alt="Signature"
                    style={{ maxHeight: "48px", maxWidth: "140px", objectFit: "contain", border: "1px solid #ddd" }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='48' viewBox='0 0 140 48'%3E%3Crect fill='%23f0f0f0' width='140' height='48'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23999' font-size='11'%3ESignature%3C/text%3E%3C/svg%3E";
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleRemoveAuthorizedSignature}
                    style={{ ...styles.cancelButton, padding: "8px 16px", fontSize: "13px" }}
                  >
                    Remove Signature
                  </button>
                </div>
              )}
              <div style={{ marginBottom: "12px" }}>
                <p style={{ fontSize: "12px", color: "#555", marginBottom: "6px" }}>Draw your signature:</p>
                <div style={{ border: "1px solid #ccc", borderRadius: "4px", overflow: "hidden", background: "#fff", maxWidth: "320px" }}>
                  <SignatureCanvas
                    ref={signaturePadRef}
                    penColor="black"
                    canvasProps={{
                      width: isMobile ? 280 : 320,
                      height: 120,
                      style: { display: "block", width: "100%", height: "120px" },
                    }}
                  />
                </div>
                <div style={{ display: "flex", gap: "8px", marginTop: "8px", flexWrap: "wrap" }}>
                  <button type="button" onClick={handleClearSignaturePad} style={{ ...styles.cancelButton, padding: "8px 14px", fontSize: "13px" }}>
                    Clear
                  </button>
                  <button type="button" onClick={handleSaveDrawnSignature} style={{ ...styles.submitButton, padding: "8px 14px", fontSize: "13px" }}>
                    Use this signature
                  </button>
                </div>
              </div>
              <p style={{ fontSize: "12px", color: "#666", marginBottom: "6px" }}>Or upload an image:</p>
              <input
                type="file"
                accept="image/*"
                onChange={handleAuthorizedSignatureChange}
                style={styles.fileInput}
              />
              {validationErrors.authorizedSignature && (
                <span style={styles.error}>{validationErrors.authorizedSignature}</span>
              )}
            </div>

            {/* Bank Account Details Section */}
            <div style={styles.bankAccountFormSection}>
              <h3 style={styles.sectionTitle}>Bank Account Details</h3>

              <div style={{
                ...styles.formRow,
                display: isMobile ? "flex" : "grid",
                flexDirection: isMobile ? "column" : "row",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: isMobile ? "16px" : "20px",
              }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Bank Name <span style={styles.optional}>(Optional)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.bankName}
                    onChange={(e) =>
                      setFormData({ ...formData, bankName: e.target.value })
                    }
                    style={{
                      ...styles.input,
                      borderColor: validationErrors.bankName ? "#dc3545" : "#e9edef",
                      width: "100%",
                    }}
                    placeholder="Enter bank name"
                  />
                  {validationErrors.bankName && (
                    <span style={styles.error}>{validationErrors.bankName}</span>
                  )}
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Account Holder Name <span style={styles.optional}>(Optional)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.accountHolderName}
                    onChange={(e) =>
                      setFormData({ ...formData, accountHolderName: e.target.value })
                    }
                    style={{
                      ...styles.input,
                      borderColor: validationErrors.accountHolderName ? "#dc3545" : "#e9edef",
                      width: "100%",
                    }}
                    placeholder="Enter account holder name"
                  />
                  {validationErrors.accountHolderName && (
                    <span style={styles.error}>{validationErrors.accountHolderName}</span>
                  )}
                </div>
              </div>

              <div style={{
                ...styles.formRow,
                display: isMobile ? "flex" : "grid",
                flexDirection: isMobile ? "column" : "row",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: isMobile ? "16px" : "20px",
              }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Account Number <span style={styles.optional}>(Optional)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.accountNumber}
                    onChange={(e) =>
                      setFormData({ 
                        ...formData, 
                        accountNumber: e.target.value.replace(/\D/g, "") 
                      })
                    }
                    maxLength="18"
                    style={{
                      ...styles.input,
                      borderColor: validationErrors.accountNumber ? "#dc3545" : "#e9edef",
                      width: "100%",
                    }}
                    placeholder="Enter account number"
                  />
                  {validationErrors.accountNumber && (
                    <span style={styles.error}>{validationErrors.accountNumber}</span>
                  )}
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    IFSC Code <span style={styles.optional}>(Optional)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.ifscCode}
                    onChange={(e) =>
                      setFormData({ 
                        ...formData, 
                        ifscCode: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").substring(0, 11)
                      })
                    }
                    maxLength="11"
                    style={{
                      ...styles.input,
                      borderColor: validationErrors.ifscCode ? "#dc3545" : "#e9edef",
                      width: "100%",
                    }}
                    placeholder="Enter IFSC code (e.g., ABCD0123456)"
                  />
                  {validationErrors.ifscCode && (
                    <span style={styles.error}>{validationErrors.ifscCode}</span>
                  )}
                </div>
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                  style={styles.checkbox}
                />
                Active
              </label>
            </div>

              <div style={{
                ...styles.formActions,
                marginTop: isMobile ? "8px" : "16px",
                flexWrap: isMobile ? "wrap" : "nowrap",
              }}>
                {isEditMode && (
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditMode(false);
                      // Reset form to current business data
                      if (business) {
                        setFormData({
                          companyName: business.companyName || "",
                          address: business.address || "",
                          gstin: business.gstin || "",
                          state: business.state || "",
                          city: business.city || "",
                          pincode: business.pincode || "",
                          email: business.email || "",
                          phone: business.phone || "",
                          pan: business.pan || "",
                          bankName: business.bankName || "AXIS BANK, HATKESHWAR",
                          accountNumber: business.accountNumber || "917020062510731",
                          ifscCode: business.ifscCode || "UTIB0002645",
                          accountHolderName: business.accountHolderName || "RAHUL TRADERS",
                          companyLogo: null,
                          companyLogoPreview: business.companyLogo?.url ? getUploadImageUrl(business.companyLogo.url) : null,
                          authorizedSignature: null,
                          authorizedSignaturePreview: business.authorizedSignature?.url ? getUploadImageUrl(business.authorizedSignature.url) : null,
                          isActive: business.isActive !== undefined ? business.isActive : true,
                        });
                      }
                      setValidationErrors({});
                    }}
                    style={{
                      ...styles.cancelButton,
                      padding: isMobile ? "12px 20px" : "12px 24px",
                      flex: isMobile ? "1" : "none",
                      minWidth: isMobile ? "0" : "100px",
                    }}
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  disabled={saveLoading}
                  style={{
                    ...styles.submitButton,
                    padding: isMobile ? "12px 20px" : "12px 24px",
                    flex: isMobile ? "1" : "none",
                    minWidth: isMobile ? "0" : "140px",
                  }}
                >
                  {saveLoading ? "Saving..." : business || isEditMode ? "Update Business" : "Save Business"}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      <BottomNavbar />
    </div>
  );
};

const styles = {
  container: {
    minHeight: "100vh",
    backgroundColor: "#f0f2f5",
    display: "flex",
    flexDirection: "column",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Helvetica, 'Lucida Grande', Arial, Ubuntu, Cantarell, 'Fira Sans', sans-serif",
    paddingBottom: "80px",
  },
  content: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "24px",
    minHeight: "calc(100vh - 80px)",
  },
  pageHeader: {
    marginBottom: "24px",
    paddingBottom: "16px",
  },
  pageTitle: {
    fontSize: "32px",
    fontWeight: "bold",
    color: "#111b21",
    margin: 0,
    letterSpacing: "-0.02em",
  },
  businessCard: {
    backgroundColor: "#ffeef2",
    borderRadius: "16px",
    padding: "32px",
    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
    border: "1px solid #ffd6e0",
    marginBottom: "20px",
  },
  businessCardHeader: {
    display: "flex",
    alignItems: "flex-start",
    gap: "20px",
    marginBottom: "24px",
    paddingBottom: "20px",
    borderBottom: "1px solid #ffd6e0",
  },
  businessLogo: {
    flexShrink: 0,
  },
  logoCircle: {
    width: "60px",
    height: "60px",
    borderRadius: "50%",
    backgroundColor: "#ffffff",
    border: "3px solid #007bff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "24px",
    fontWeight: "bold",
    color: "#007bff",
    letterSpacing: "-0.01em",
  },
  businessHeaderInfo: {
    flex: 1,
    minWidth: 0,
  },
  businessNameRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "12px",
    flexWrap: "wrap",
  },
  businessName: {
    fontSize: "20px",
    fontWeight: "bold",
    color: "#111b21",
    margin: 0,
    letterSpacing: "-0.01em",
    textTransform: "uppercase",
    flex: 1,
  },
  businessHeaderActions: {
    display: "flex",
    gap: "8px",
    flexShrink: 0,
  },
  businessEditButton: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    fontSize: "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 0.2s",
    color: "#54656f",
  },
  businessDetails: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  businessDetailRow: {
    fontSize: "15px",
    fontWeight: "400",
    color: "#111b21",
    letterSpacing: "-0.01em",
    lineHeight: "22px",
  },
  businessDetailLabel: {
    fontWeight: "500",
    color: "#667781",
  },
  businessDetailValue: {
    color: "#111b21",
  },
  card: {
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    padding: "30px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    border: "1px solid #e9edef",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  formRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: "20px",
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    width: "100%",
  },
  label: {
    fontSize: "14px",
    fontWeight: "500",
    color: "#111b21",
    letterSpacing: "-0.01em",
  },
  required: {
    color: "#dc3545",
  },
  optional: {
    color: "#6c757d",
    fontSize: "12px",
    fontWeight: "normal",
  },
  input: {
    padding: "12px 16px",
    border: "1px solid #e9edef",
    borderRadius: "8px",
    fontSize: "15px",
    fontWeight: "400",
    color: "#111b21",
    backgroundColor: "#ffffff",
    outline: "none",
    transition: "border-color 0.2s",
    width: "100%",
    letterSpacing: "-0.01em",
  },
  textarea: {
    padding: "12px 16px",
    border: "1px solid #e9edef",
    borderRadius: "8px",
    fontSize: "15px",
    fontFamily: "inherit",
    resize: "vertical",
    fontWeight: "400",
    color: "#111b21",
    backgroundColor: "#ffffff",
    outline: "none",
    transition: "border-color 0.2s",
    width: "100%",
    letterSpacing: "-0.01em",
    lineHeight: "20px",
  },
  checkboxLabel: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    cursor: "pointer",
  },
  checkbox: {
    width: "18px",
    height: "18px",
    cursor: "pointer",
  },
  error: {
    color: "#dc3545",
    fontSize: "12px",
  },
  formActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    paddingTop: "16px",
    borderTop: "1px solid #e9edef",
  },
  cancelButton: {
    padding: "12px 24px",
    backgroundColor: "#f0f2f5",
    color: "#111b21",
    border: "none",
    borderRadius: "24px",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: "400",
    transition: "background-color 0.2s",
    minWidth: "100px",
  },
  submitButton: {
    padding: "12px 24px",
    backgroundColor: "#dc3545",
    color: "#ffffff",
    border: "none",
    borderRadius: "24px",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: "400",
    transition: "background-color 0.2s",
    minWidth: "140px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
  },
  bankAccountSection: {
    marginTop: "24px",
    paddingTop: "24px",
    borderTop: "1px solid #ffd6e0",
  },
  bankAccountTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#111b21",
    margin: "0 0 16px 0",
    letterSpacing: "-0.01em",
  },
  companyLogoSection: {
    marginTop: "24px",
    paddingTop: "24px",
    borderTop: "1px solid #ffd6e0",
  },
  companyLogoTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#111b21",
    margin: "0 0 16px 0",
    letterSpacing: "-0.01em",
  },
  companyLogoContainer: {
    marginBottom: "16px",
    display: "flex",
    justifyContent: "flex-start",
  },
  companyLogo: {
    width: "120px",
    height: "120px",
    objectFit: "contain",
    borderRadius: "8px",
    border: "1px solid #e9edef",
    backgroundColor: "#ffffff",
    padding: "8px",
  },
  companyLogoFormSection: {
    marginTop: "24px",
    paddingTop: "24px",
    borderTop: "1px solid #e9edef",
  },
  bankDetails: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  bankAccountFormSection: {
    marginTop: "24px",
    paddingTop: "24px",
    borderTop: "1px solid #e9edef",
  },
  sectionTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#111b21",
    margin: "0 0 20px 0",
    letterSpacing: "-0.01em",
  },
  imagePreviewContainer: {
    position: "relative",
    display: "inline-block",
    marginBottom: "12px",
  },
  imagePreview: {
    width: "120px",
    height: "60px",
    objectFit: "contain",
    borderRadius: "8px",
    border: "1px solid #e9edef",
    backgroundColor: "#f0f2f5",
    padding: "8px",
  },
  removeImageButton: {
    position: "absolute",
    top: "-8px",
    right: "-8px",
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    backgroundColor: "#dc3545",
    color: "#ffffff",
    border: "none",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
    transition: "transform 0.2s",
    lineHeight: "1",
  },
  fileInput: {
    display: "none",
  },
  fileInputLabel: {
    display: "inline-block",
    padding: "10px 20px",
    backgroundColor: "#f0f2f5",
    color: "#111b21",
    border: "1px solid #e9edef",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "400",
    transition: "background-color 0.2s",
    textAlign: "center",
    width: "100%",
    maxWidth: "200px",
  },
};

export default MyBusiness;

